<template>
  <o-dialog
    ref="dialog"
    :title="$t('customer.add')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('customer.add'), type: 'confirm', callback: handleConfirm },
    ]"
    :portal="portal"
    :valid="validate"
  >
    <div class="pt-4 text-left">
      <o-form ref="form">
        <o-field-group v-slot="{ status }" :label="$t('general.customer')" :name="$t('general.customer')" :validation-value="customer" class="mb-4 block" rules="required">
          <customer-select v-model="customer" create :status="status" />
        </o-field-group>

        <o-field-group v-slot="{ status }" :label="$t('general.price')" :name="$t('general.price')" :validation-value="price" class="mb-4 block" rules="required">
          <text-field v-model="price" :leading="$auth.info.location.currencySymbol" :status="status" />
        </o-field-group>

        <o-field-group v-if="customerPasses && customerPasses.length" :label="$t('general.pass')" :name="$t('general.pass')" class="mb-4 block">
          <discount-select
            :service="service"
            :employee="service && service.employee"
            v-model:customer-pass="customerPass"
            :customer-passes="customerPasses"
            :placeholder="$t('pass.select')"
            :auto-select="false"
            class="w-full"
          />
        </o-field-group>

        <template v-if="resources">
          <o-field-group
            v-for="resource in resources"
            :key="resource.serviceResource.idServiceResource"
            :label="resource.serviceResource.name"
            :name="resource.serviceResource.name"
            class="mb-4 block"
          >
            <employee-select
              v-model="resource.resource"
              class="w-full bg-white shadow-sm rounded-md"
              :filter="($event) => resourceFilter($event, resource.serviceResource)"
              :service="service"
              :date="date"
              :availability-options="{
                service,
                date,
                resource,
                startTime: service.startTime
              }"
              allow-none
            />
          </o-field-group>
        </template>

        <template v-if="reservation.recurrenceGuid">
          <o-field-group
            :label="$t('reservation.recurrence')"
            :name="$t('reservation.recurrence')"
            class="mb-4 block"
          >
            <div v-if="customer" class="border rounded-md shadow-sm">
              <div class="flex items-center justify-between p-2">
                <p class="opacity-80 font-medium text-sm">
                  {{ $t('components.reservation.actions.attendeeAdd.enableRecurrence') }}
                </p>

                <toggle-field v-model="recurrence" />
              </div>

              <div v-if="recurrence" class="p-2 border-t">
                <recur-customer :reservation="reservation" v-model:reservations="reservations" v-model:customer="customer" auto />
              </div>
            </div>

            <div v-else class="border rounded-md shadow-sm">
              {{ $t('components.reservation.actions.attendeeAdd.selectCustomer') }}
            </div>
          </o-field-group>
        </template>

        <o-field-group :label="$t('general.notes')" :name="$t('general.notes')" class="mb-4 block">
          <text-area-field v-model="notes" />
        </o-field-group>

        <o-custom-fields v-if="customFields" :data="customFields" class="w-full mt-4">
          <template #default="{fields}">
            <div
              v-for="field in fields"
              :key="field.props.id"
              class="flex space-x-4 mb-4"
            >
              <o-field-group class="w-full" :label="field.label">
                <component
                  :is="field.component"
                  v-bind="field.props"
                  v-on="field.events"
                />
              </o-field-group>
            </div>
          </template>
        </o-custom-fields>

        <o-field-group
          :label="$t('general.sendEmail')"
          :info="$t('components.reservation.sendEmailInfo')"
          class="w-full mb-4 block"
        >
          <toggle-field v-model="sendEmail" />
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'

import CustomerSelect from '@/components/customer/elements/Select.vue'
import DiscountSelect from '@/components/reservation/elements/DiscountSelect.vue'
import EmployeeSelect from '@/components/employee/elements/Select.vue'
import RecurCustomer from '@/components/recurrence/elements/Customer.vue'
import {
  OFieldGroup,
  OCustomFields,
} from '@/components/elements'
import {
  TextAreaField,
  TextField,
  ToggleField,
} from '@/components/fields'

import { ReservationServiceModel } from '@/_api/models/reservationServiceModel'
import { ConfirmationRequest } from '@/_api/models/booking'
import { ServiceResourceAssignment } from '@/_api/models/serviceResource'

import { useCustomFieldsStore } from '~/stores/useCustomFieldsStore'
import { useServiceStore } from '~/stores/useServiceStore'

interface Props {
  reservation: Record<string, any>
  portal?: string | null
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'update:reservation', value: any): void
  (e: 'update'): void
  (e: 'success'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: null,
  loading: false,
})

const emit = defineEmits<Emits>()

const { $api, $notification, $bus, $i18n, $features, $grants } = useNuxtApp() as any
const { $auth } = useNuxtApp() as any

const customFieldsStore = useCustomFieldsStore()
const serviceStore = useServiceStore()

const dialog = ref<any>()
const form = ref<any>()

const customer = ref<any>(null)
const customerPass = ref<any>(null)
const customerPasses = ref<any[]>([])
const canViewPasses = computed(() => $features.get('passes') && $grants.all({ pass: ['view'] }))
const price = ref(props.reservation.price)
const notes = ref('')
const resources = ref<any>(null)
const recurrence = ref(false)
const reservations = ref<any[]>([])
const sendEmail = ref(false)
const customFields = ref<any>(null)

const info = computed(() => {
  return $i18n.t('components.reservation.actions.attendeeAdd.info', { label: props.reservation.infoLabel })
})

const service = computed(() => {
  return props.reservation?.services?.[0]
})

const date = computed(() => {
  return props.reservation?.date?.value
})

const startTime = computed(() => {
  return props.reservation?.startTime
})

onMounted(() => {
  customFieldsStore.sync()
})

watch(customer, async (newCustomer) => {
  customerPass.value = null
  customerPasses.value = []

  if (newCustomer && !newCustomer.isNewRecord && canViewPasses.value) {
    try {
      const passes = await $api.customer.passes(String(newCustomer.idCustomer))
      customerPasses.value = passes.items || []
    } catch (e) {
      customerPasses.value = []
    }
  }
})

function open () {
  customFields.value = customFieldsStore.reservationCustomer
  const reservation = props.reservation
  const services = reservation.services
  const reservationService = services?.[0] || null

  if (reservationService && reservationService.service) {
    const idService = reservationService.service.idService
    const svc = serviceStore.getServiceByID(idService) || null

    if (svc) {
      const rs = new ReservationServiceModel()

      rs.service = svc

      rs.setResources(ServiceResourceAssignment.customer)

      resources.value = rs.resources
    }
  }

  customer.value = null
  customerPass.value = null
  customerPasses.value = []
  notes.value = ''
  price.value = reservation.price
  sendEmail.value = $auth.info?.location?.sendInternalReservationCreateNotifications || false

  dialog.value.open()
}

async function handleConfirm () {
  emit('update:loading', true)

  if (customer.value.isNewRecord) {
    const result = await saveCustomer()

    if (!result) {
      emit('update:loading', false)

      return
    }
  }

  const reservationCustomer = await createReservationCustomer()

  emit('update:loading', false)

  if (reservationCustomer) {
    return true
  }
}

async function createReservationCustomer () {
  try {
    const reservation = props.reservation

    const data: any = {
      customer: customer.value,
      price: price.value,
      notes: notes.value,
      resources: resources.value,
      customFields: customFields.value,
    }

    if (recurrence.value) {
      const mappedReservations = reservations.value
        .map(({ reservation, action }: any) => {
          return {
            idReservation: reservation.idReservation,
            date: reservation.date,
            action: action.value,
          }
        })

      data.customerRecurrence = { reservations: mappedReservations }
    }

    const request = ConfirmationRequest.fromGroupReservation(reservation, data)
    for (const service of request.services) {
      service.customerPass = customerPass.value
    }

    const confirmResponse = await $api.booking.confirm(request, { sendEmail: sendEmail.value })

    if (confirmResponse) {
      const saturateResponse = await $api.reservation.getWithDate(
        String(props.reservation.idReservation),
        props.reservation.date.integer,
      )
      const updatedReservation = saturateResponse.data

      emit('update:reservation', updatedReservation)
      emit('update:loading', false)

      emit('update')

      $notification.toast({
        title: $i18n.t('components.reservation.actions.attendeeAdd.success'),
        text: $i18n.t('components.reservation.actions.attendeeAdd.successInfo', { label: updatedReservation.customerInfoLabel }),
        variant: 'success',
      })

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e.errors })
  }
}

async function saveCustomer () {
  try {
    const response = await $api.customer.save(customer.value)
    const savedCustomer = response.data

    if (savedCustomer) {
      customer.value = savedCustomer

      $notification.toast({
        title: $i18n.t('customer.createdSuccess'),
        text: $i18n.t('customer.createdSuccessInfo', { name: savedCustomer.displayName }),
        variant: 'success',
      })

      $bus.emit.created('Customer', savedCustomer)

      return true
    }
  } catch (e) {
    $notification.toast({
      title: $i18n.t('general.error'),
      text: $i18n.t('customer.createError'),
      variant: 'danger',
    })
  }
}

function validate () {
  return form.value.validate()
}

function resourceFilter (resource: any, serviceResource: any) {
  return resource && serviceResource ? serviceResource.resourceIDs.includes(resource.idEmployee) : false
}

defineExpose({ open })
</script>
